
import React, { useState, useEffect } from "react";
import {
  Search,
  ChevronLeft,
  ChevronRight,
  CloudDownload,
  X,
  Check,
} from "lucide-react";
import "../Style/ECUOverAirUpdatesLogs.css";

// --- Helper Functions ---
const calculateDuration = (start, end) => {
  if (!start || !end) return "N/A";
  const startDate = new Date(start);
  const endDate = new Date(end);
  if (isNaN(startDate) || isNaN(endDate)) return "Invalid Date";

  let diff = Math.abs(endDate - startDate) / 1000;
  const minutes = Math.floor(diff / 60);
  const seconds = Math.floor(diff % 60);

  let result = "";
  if (minutes > 0) result += `${minutes}m `;
  result += `${seconds}s`;
  return result.trim();
};

// --- Mock Data ---
const mockFirmwareData = [
  {
    id: "FW001",
    deviceName: "DEV_TSUYOTES01",
    RequestPending: "2025-09-16T10:00:00Z",
    RequestReceived: "2025-09-16T10:00:05Z",
    PackageDownloading: "2025-09-16T10:00:15Z",
    UpdateStaging: "2025-09-16T10:01:45Z",
    WaitingForPreconditions: "2025-09-16T10:01:55Z",
    UpdateinProgress: "2025-09-16T10:02:10Z",
    UpdateComplete: "2025-09-16T10:03:30Z",
    status: "Success",
    AprovalTime: "2025-09-16T10:04:00Z",
    ApprovedBy: "AdminUser",
    createdBy: "System",
    TotalTime: "3m 30s",
  },
  {
    id: "FW002",
    deviceName: "DEV_TSUYOTES03",
    RequestPending: "2025-09-16T11:30:00Z",
    RequestReceived: "2025-09-16T11:30:08Z",
    PackageDownloading: "2025-09-16T11:30:20Z",
    UpdateStaging: "2025-09-16T11:32:00Z",
    WaitingForPreconditions: null,
    UpdateinProgress: "2025-09-16T11:32:15Z",
    UpdateComplete: "2025-09-16T11:34:00Z",
    status: "Failed",
    AprovalTime: "2025-09-16T11:34:30Z",
    ApprovedBy: "AdminUser",
    createdBy: "System",
    TotalTime: "4m 0s",
  },
  // ... add more mock data as needed
];

// --- Sub-Components ---

const ParameterLogsTable = ({ devices }) => {
  const parameterColumns = [
    "Device Name",
    "Device Id",
    "Vehicle Number",
    "IMEI",
    "SIM Number",
    "VIN Number",
    "Customer ID",
    "Change By",
    "Parameter Update",
    "Last Change",
    "Last Time",
  ];

  return (
    <div className="ECU-table-wrapper">
      <table className="ECU-table">
        <thead className="ECU-table-head">
          <tr>
            {parameterColumns.map((col) => (
              <th key={col} className="ECU-table-cell">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {devices.length > 0 ? (
            devices.map((device) => (
              <tr key={device.DeviceID}>
                <td className="ECU-table-cell">{device.DeviceName ?? "N/A"}</td>
                <td className="ECU-table-cell">{device.DeviceID ?? "N/A"}</td>
                <td className="ECU-table-cell">{device.VehicleID ?? "N/A"}</td>
                <td className="ECU-table-cell">{device.IMEI ?? "N/A"}</td>
                <td className="ECU-table-cell">{device.SIM ?? "N/A"}</td>
                <td className="ECU-table-cell">{device.VIN ?? "N/A"}</td>
                <td className="ECU-table-cell">{device.CustomerID ?? "N/A"}</td>
                <td className="ECU-table-cell">AdminUsed</td>
                <td className="ECU-table-cell">Harsh Acceleration</td>
                <td className="ECU-table-cell">Config Change</td>
                <td className="ECU-table-cell">
                  {new Date().toLocaleString()}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={parameterColumns.length}
                className="ECU-empty-message"
              >
                No Devices Found
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

const FirmwareLogsTable = () => {
  const firmwareDurationColumns = [
    "DeviceName",
    "PendingReceived",
    "ReceivedDownloading",
    "DownloadingStaging",
    "StagingPreconditions",
    "PreconditionsInProgress",
    "InProgressComplete",
    "RolloutCreativeTime",
    "Status",
    "AprovalTime",
    "ApprovedBy",
    "createdBy",
    "TotalTime",
  ];

  return (
    <div className="ECU-table-wrapper">
      <table className="ECU-table">
        <thead className="ECU-table-head">
          <tr>
            {firmwareDurationColumns.map((col) => (
              <th key={col} className="ECU-table-cell">
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {mockFirmwareData.map((log) => (
            <tr key={log.id}>
              <td className="ECU-table-cell">{log.deviceName}</td>
              <td className="ECU-table-cell">
                {calculateDuration(log.RequestPending, log.RequestReceived)}
              </td>
              <td className="ECU-table-cell">
                {calculateDuration(log.RequestReceived, log.PackageDownloading)}
              </td>
              <td className="ECU-table-cell">
                {calculateDuration(log.PackageDownloading, log.UpdateStaging)}
              </td>
              <td className="ECU-table-cell">
                {calculateDuration(
                  log.UpdateStaging,
                  log.WaitingForPreconditions,
                )}
              </td>
              <td className="ECU-table-cell">
                {calculateDuration(
                  log.WaitingForPreconditions,
                  log.UpdateinProgress,
                )}
              </td>
              <td className="ECU-table-cell">
                {calculateDuration(log.UpdateinProgress, log.UpdateComplete)}
              </td>
              <td className="ECU-table-cell">
                {calculateDuration(log.RequestPending, log.UpdateComplete)}
              </td>
              <td className="ECU-table-cell font-bold">{log.status}</td>
              <td className="ECU-table-cell">
                {log.AprovalTime
                  ? new Date(log.AprovalTime).toLocaleString()
                  : "N/A"}
              </td>
              <td className="ECU-table-cell">{log.ApprovedBy || "N/A"}</td>
              <td className="ECU-table-cell">{log.createdBy || "N/A"}</td>
              <td className="ECU-table-cell">{log.TotalTime}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// --- Main Component ---

const ECUOverAirUpdatesLogs = () => {
  const deviceColumns = [
    "Device Name",
    "Device Id",
    "Vehicle Number",
    "IMEI",
    "SIM Number",
    "VIN Number ",
    "Customer ID",
    "Download Logs",
  ];

  // State Management
  const [devices, setDevices] = useState([]);
  const [filteredDevices, setFilteredDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeLogType, setActiveLogType] = useState("Device"); // 'Device', 'Parameter', 'Firmware'

  // Modal & Notification State
  const [showDeviceModal, setShowDeviceModal] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    type: "",
  });

  // Initial Fetch
  useEffect(() => {
    fetchDevices();
  }, []);

  // Search Logic
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredDevices(devices);
    } else {
      const filtered = devices.filter((device) =>
        device.DeviceName.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredDevices(filtered);
    }
  }, [searchTerm, devices]);

  // Notifications
  const showNotification = (message, type = "info") => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 5000);
  };

  // API Call: Fetch Devices
  const fetchDevices = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");

      // Note: You might want to handle no token more gracefully in production
      if (!token) {
        // setError("Authentication token not found.");
        // setLoading(false);
        // return;
        console.warn(
          "No token found, proceeding (remove this warning in prod)",
        );
      }

      const response = await fetch(
        "https://api.yellowmatrix.co.in/devicereg/assigned",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok)
        throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();

      if (data.Devices && Array.isArray(data.Devices)) {
        const formattedDevices = data.Devices.map((device) => ({
          DeviceName: device.Vehicle.VehicleID,
          DeviceID: device.DeviceID,
          VehicleID: device.Vehicle.VehicleID,
          IMEI: device.IMEI,
          SIM: device.ICCID,
          VIN: device.Vehicle.VIM,
          CustomerID: device.Customerid,
        }));
        setDevices(formattedDevices);
        setFilteredDevices(formattedDevices);
      } else {
        // Fallback for demo purposes if API fails or is empty
        console.log("No devices found or API error, using empty state");
        setDevices([]);
        setFilteredDevices([]);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // API Call: Download Logs
  const handleDownloadLogs = async (deviceId, customerId, deviceName) => {
    setDownloading(deviceId);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch("https://api.yellowmatrix.co.in/getlogs", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ customerid: customerId, deviceid: deviceId }),
      });

      if (!response.ok) throw new Error("Failed to fetch logs");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `logs_${deviceName}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showNotification(`Logs downloaded for ${deviceName}`, "success");
    } catch (err) {
      showNotification(`Failed: ${err.message}`, "error");
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadSelectedLogs = () => {
    if (selectedDevice) {
      handleDownloadLogs(
        selectedDevice.DeviceID,
        selectedDevice.CustomerID,
        selectedDevice.DeviceName,
      );
    } else {
      showNotification("Please select a device first", "error");
    }
  };

  const renderLogTable = () => {
    switch (activeLogType) {
      case "Parameter":
        return <ParameterLogsTable devices={filteredDevices} />;
      case "Firmware":
        return <FirmwareLogsTable />;
      case "Device":
      default:
        return (
          <div className="ECU-table-wrapper">
            <table className="ECU-table">
              <thead className="ECU-table-head">
                <tr>
                  {deviceColumns.map((col) => (
                    <th key={col} className="ECU-table-cell">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredDevices.length > 0 ? (
                  filteredDevices.map((device) => (
                    <tr key={device.DeviceID}>
                      <td className="ECU-table-cell">
                        {device.DeviceName ?? "N/A"}
                      </td>
                      <td className="ECU-table-cell">
                        {device.DeviceID ?? "N/A"}
                      </td>
                      <td className="ECU-table-cell">
                        {device.VehicleID ?? "N/A"}
                      </td>
                      <td className="ECU-table-cell">{device.IMEI ?? "N/A"}</td>
                      <td className="ECU-table-cell">{device.SIM ?? "N/A"}</td>
                      <td className="ECU-table-cell">{device.VIN ?? "N/A"}</td>
                      <td className="ECU-table-cell">
                        {device.CustomerID ?? "N/A"}
                      </td>
                      <td className="ECU-table-cell">
                        <button
                          onClick={() =>
                            handleDownloadLogs(
                              device.DeviceID,
                              device.CustomerID,
                              device.DeviceName,
                            )
                          }
                          className="ECU-download-icon-btn"
                          disabled={downloading === device.DeviceID}
                        >
                          {downloading === device.DeviceID ? (
                            <span className="loader-spin"></span>
                          ) : (
                            <CloudDownload size={20} />
                          )}
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={deviceColumns.length}
                      className="ECU-empty-message"
                    >
                      {searchTerm
                        ? "No devices match your search"
                        : "No Devices Found"}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
    }
  };

  return (
    <div className="ECU-container">
      {/* Notification Toast */}
      {notification.show && (
        <div className={`ECU-notification notification-${notification.type}`}>
          <span>{notification.message}</span>
          <button
            onClick={() => setNotification({ ...notification, show: false })}
            className="ECU-notification-close"
          >
            <X size={18} />
          </button>
        </div>
      )}

      {/* Device Selection Modal */}
      {showDeviceModal && (
        <div className="ECU-modal-overlay">
          <div className="ECU-modal-container">
            <div className="ECU-modal-header">
              <h2>Choose Device</h2>
              <button
                onClick={() => setShowDeviceModal(false)}
                className="ECU-modal-close"
              >
                <X size={24} />
              </button>
            </div>
            <div className="ECU-modal-content">
              {devices.map((device) => (
                <div
                  key={device.DeviceID}
                  className={`ECU-device-item ${selectedDevice?.DeviceID === device.DeviceID ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedDevice(device);
                    setShowDeviceModal(false);
                  }}
                >
                  <div>
                    <div className="font-bold">{device.DeviceName}</div>
                    <div className="text-sm text-gray-500">
                      ID: {device.DeviceID}
                    </div>
                  </div>
                  {selectedDevice?.DeviceID === device.DeviceID && (
                    <Check size={16} color="green" />
                  )}
                </div>
              ))}
            </div>
            <div className="ECU-modal-footer">
              <button
                onClick={() => setShowDeviceModal(false)}
                className="ECU-modal-btn cancel"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header Section */}
      <div className="ECU-header-section">
        <h1 className="ECU-title">ECU Logs</h1>
        <p className="ECU-subtitle">Manage and monitor ECU rollouts.</p>
        <hr className="ECU-line-bar" />
      </div>

      {/* Selected Device Banner (Optional) */}
      {selectedDevice && (
        <div className="ECU-selected-banner">
          <span>
            Selected: <strong>{selectedDevice.DeviceName}</strong>
          </span>
          <button
            onClick={() => setSelectedDevice(null)}
            className="ECU-clear-btn"
          >
            Clear
          </button>
        </div>
      )}

      {/* Controls & Tabs */}
      <div className="ECU-controls">
        <div className="ECU-tabs">
          <button
            className={`ECU-tab-btn ${activeLogType === "Device" ? "active" : ""}`}
            onClick={() => setActiveLogType("Device")}
          >
            Device Logs
          </button>
          <button
            className={`ECU-tab-btn ${activeLogType === "Parameter" ? "active" : ""}`}
            onClick={() => setActiveLogType("Parameter")}
          >
            Parameter Logs
          </button>
          <button
            className={`ECU-tab-btn ${activeLogType === "Firmware" ? "active" : ""}`}
            onClick={() => setActiveLogType("Firmware")}
          >
            Firmware Logs
          </button>
          <button
            className="ECU-action-btn"
            onClick={() => setShowDeviceModal(true)}
          >
            CHOOSE DEVICE
          </button>
          <button
            className="ECU-action-btn"
            onClick={handleDownloadSelectedLogs}
            disabled={!selectedDevice || downloading}
          >
            {downloading ? "DOWNLOADING..." : "DOWNLOAD LOGS"}
          </button>
        </div>

        <div className="ECU-actions">{/* Search Input */}</div>
      </div>

      {/* Data Table Area */}
      <div className="ECU-content-area">
        <div className="ECU-search-box">
          <Search className="ECU-search-icon" size={18} />
          <input
            type="text"
            className="ECU-search-input"
            placeholder="Search Device..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={activeLogType === "Firmware"}
          />
        </div>

        {loading ? (
          <div className="ECU-loading">Loading data...</div>
        ) : error ? (
          <div className="ECU-error">
            Error: {error}
            <button onClick={fetchDevices} className="ECU-retry-btn">
              Retry
            </button>
          </div>
        ) : (
          <>
            {renderLogTable()}

            {/* Pagination Controls */}
            <div className="ECU-pagination">
              <span>
                Rows per page:{" "}
                <select>
                  <option>5</option>
                  <option>10</option>
                  <option>20</option>
                </select>
              </span>

              <div className="ECU-page-nav">
                <span>
                  1-{filteredDevices.length} of {filteredDevices.length}
                </span>
                <button className="ECU-nav-btn" disabled>
                  <ChevronLeft size={16} />
                </button>
                <button className="ECU-nav-btn" disabled>
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ECUOverAirUpdatesLogs;
